import React from 'react';
import { motion, Variants } from 'framer-motion';
import { Check, X, Gauge, Volume2, Sparkles, AlertCircle } from 'lucide-react';
import { LivingInAreaData } from '../../types/livingInArea';

// --- Animation Variants ---
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 300, damping: 24 },
  },
};

interface LifestyleSectionProps {
  data: LivingInAreaData['lifestyleOverview'];
}

export const LifestyleSection: React.FC<LifestyleSectionProps> = ({ data }) => {
  return (
    <section className="w-full max-w-5xl mx-auto py-16 px-6 bg-white">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
        variants={containerVariants}
        className="space-y-12"
      >
        {/* Header Section */}
        <div className="text-center space-y-4 max-w-2xl mx-auto">
          <motion.div variants={itemVariants}>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-100 text-blue-800 text-xs font-bold uppercase tracking-widest">
              <Sparkles size={14} className="text-blue-600" />
              Area Insights
            </span>
          </motion.div>
          <motion.h2
            variants={itemVariants}
            className="text-4xl font-black tracking-tight text-slate-900 md:text-5xl"
          >
            Lifestyle <span className="text-blue-600">Compatibility</span>
          </motion.h2>
          <motion.p variants={itemVariants} className="text-lg font-medium text-slate-600">
            Discover if the rhythm of this neighborhood matches your daily flow.
          </motion.p>
        </div>

        {/* Comparison Grid */}
        <div className="grid gap-8 md:grid-cols-2">
          {/* Pros Card */}
          <motion.div
            variants={itemVariants}
            className="relative overflow-hidden rounded-3xl border-2 border-slate-100 bg-slate-50/50 p-8 shadow-sm"
          >
            <div className="relative z-10">
              <div className="mb-8 flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-600 text-white shadow-lg shadow-green-200">
                  <Check size={24} strokeWidth={3} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">Perfect For</h3>
                  <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide">
                    High Match
                  </p>
                </div>
              </div>

              <ul className="space-y-5">
                {data.bestFor.map((item, i) => (
                  <motion.li key={i} className="flex items-start gap-3 text-slate-800">
                    <Check className="mt-1 h-5 w-5 flex-shrink-0 text-green-600" />
                    <span className="font-semibold leading-relaxed">{item}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Cons Card */}
          <motion.div
            variants={itemVariants}
            className="relative overflow-hidden rounded-3xl border-2 border-slate-100 bg-slate-50/50 p-8 shadow-sm"
          >
            <div className="relative z-10">
              <div className="mb-8 flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-600 text-white shadow-lg shadow-amber-200">
                  <AlertCircle size={24} strokeWidth={3} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">Trade-offs</h3>
                  <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide">
                    Potential Conflict
                  </p>
                </div>
              </div>

              <ul className="space-y-5">
                {data.notIdealFor.map((item, i) => (
                  <motion.li key={i} className="flex items-start gap-3 text-slate-800">
                    <X className="mt-1 h-5 w-5 flex-shrink-0 text-amber-600" />
                    <span className="font-semibold leading-relaxed">{item}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>

        {/* Metrics Row */}
        <div className="grid gap-6 md:grid-cols-2">
          {data.paceOfLife && (
            <StatCard
              icon={<Gauge size={24} />}
              label="Pace of Life"
              value={data.paceOfLife}
              accentColor="blue"
            />
          )}
          {data.noiseLevels && (
            <StatCard
              icon={<Volume2 size={24} />}
              label="Noise Levels"
              value={data.noiseLevels}
              accentColor="purple"
            />
          )}
        </div>
      </motion.div>
    </section>
  );
};

// Simplified StatCard with better visibility
const StatCard = ({
  icon,
  label,
  value,
  accentColor,
}: {
  icon: any;
  label: string;
  value: string;
  accentColor: string;
}) => {
  const colors: Record<string, string> = {
    blue: 'bg-blue-600 text-white shadow-blue-100',
    purple: 'bg-purple-600 text-white shadow-purple-100',
  };

  return (
    <div className="flex items-center gap-6 p-6 rounded-3xl border-2 border-slate-100 bg-white shadow-sm transition-hover hover:border-slate-200">
      <div
        className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl shadow-xl ${colors[accentColor]}`}
      >
        {icon}
      </div>
      <div className="flex flex-col">
        <span className="text-xs font-black uppercase tracking-widest text-slate-500">{label}</span>
        <span className="text-2xl font-black text-slate-900 capitalize">{value}</span>
      </div>
    </div>
  );
};
